import type { DashboardSummaryOut } from '@myrentalmate/api-client';

export interface AttentionItem {
  severity: 'urgent' | 'review';
  text: string;
  to: string;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** What someone should look at today, most pressing first. Nothing when there's nothing to do. */
export function attentionItems(s: DashboardSummaryOut): AttentionItem[] {
  const items: (AttentionItem | false)[] = [
    s.licenses_expired > 0 && {
      severity: 'urgent',
      text: `${plural(s.licenses_expired, 'license has', 'licenses have')} expired`,
      to: '/staff/compliance',
    },
    s.court_dates_next_7_days > 0 && {
      severity: 'urgent',
      text: `${plural(s.court_dates_next_7_days, 'court date is', 'court dates are')} in the next 7 days`,
      to: '/staff/compliance/calendar',
    },
    s.violations_past_cure > 0 && {
      severity: 'urgent',
      text: `${plural(s.violations_past_cure, 'lease violation is', 'lease violations are')} past the cure date`,
      to: '/staff/compliance/violations',
    },
    s.work_orders_emergency > 0 && {
      severity: 'urgent',
      text: `${plural(s.work_orders_emergency, 'emergency work order is', 'emergency work orders are')} open`,
      to: '/staff/maintenance',
    },
    s.vendors_insurance_expired > 0 && {
      severity: 'urgent',
      text: `${plural(s.vendors_insurance_expired, 'vendor has', 'vendors have')} expired insurance`,
      to: '/staff/vendors',
    },
    s.leases_owing > 0 && {
      severity: 'urgent',
      text: `${plural(s.leases_owing, 'lease owes', 'leases owe')} money`,
      to: '/staff/leases',
    },
    s.bills_overdue > 0 && {
      severity: 'urgent',
      text: `${plural(s.bills_overdue, 'vendor bill is', 'vendor bills are')} past due`,
      to: '/staff/maintenance/bills',
    },
    s.work_orders_unassigned > 0 && {
      severity: 'review',
      text: `${plural(s.work_orders_unassigned, 'work order has', 'work orders have')} nobody assigned`,
      to: '/staff/maintenance',
    },
    s.licenses_expiring_30_days > 0 && {
      severity: 'review',
      text: `${plural(s.licenses_expiring_30_days, 'license expires', 'licenses expire')} within 30 days`,
      to: '/staff/compliance',
    },
    s.vendors_missing_w9 > 0 && {
      severity: 'review',
      text: `${plural(s.vendors_missing_w9, 'vendor who gets a 1099 has', 'vendors who get a 1099 have')} no W-9`,
      to: '/staff/vendors',
    },
    s.vendors_insurance_expiring > 0 && {
      severity: 'review',
      text: `${plural(s.vendors_insurance_expiring, 'vendor’s insurance expires', 'vendors’ insurance expires')} within 30 days`,
      to: '/staff/vendors',
    },
    s.leases_ending_soon > 0 && {
      severity: 'review',
      text: `${plural(s.leases_ending_soon, 'lease ends', 'leases end')} within 60 days`,
      to: '/staff/leases',
    },
    s.vacant_units_with_resident > 0 && {
      severity: 'review',
      text: `${plural(s.vacant_units_with_resident, 'unit is', 'units are')} marked vacant but ${s.vacant_units_with_resident === 1 ? 'has' : 'have'} a resident`,
      to: '/staff/properties',
    },
    s.occupied_units_without_resident > 0 && {
      severity: 'review',
      text: `${plural(s.occupied_units_without_resident, 'unit is', 'units are')} marked occupied with no resident on file`,
      to: '/staff/properties',
    },
    s.residents_without_unit > 0 && {
      severity: 'review',
      text: `${plural(s.residents_without_unit, 'resident has', 'residents have')} no unit assigned`,
      to: '/staff/residents',
    },
    s.properties_without_owner > 0 && {
      severity: 'review',
      text: `${plural(s.properties_without_owner, 'property has', 'properties have')} no owner on file`,
      to: '/staff/properties',
    },
  ];
  return items.filter((item) => item !== false);
}
